import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { SalesRequestDTO } from '@app/api/purchase-service-sales';
import { generateSeries } from '../../distribution.constants';
import { SalesRequestService } from '../services/sales-request.service';

interface RequestLine {
  skuCode: string;
  quantity: number | null;
  description: string;
}

@Component({
  selector: 'app-sales-request-add',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    DatePickerModule,
    InputNumberModule,
    InputTextModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './sales-request-add.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalesRequestAddComponent {
  private readonly router = inject(Router);
  private readonly service = inject(SalesRequestService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  readonly saving = signal(false);
  readonly lines = signal<RequestLine[]>([this.emptyLine()]);

  series = generateSeries('SR');
  title = '';
  scheduleDate: Date | null = null;
  description = '';

  private emptyLine(): RequestLine {
    return { skuCode: '', quantity: 1, description: '' };
  }

  addLine(): void {
    this.lines.update((lines) => [...lines, this.emptyLine()]);
  }

  removeLine(index: number): void {
    this.lines.update((lines) =>
      lines.length > 1 ? lines.filter((_, i) => i !== index) : lines,
    );
  }

  onBack(): void {
    this.router.navigate(['/distribution/sales-request']);
  }

  onSave(): void {
    const lines = this.lines();
    const valid =
      this.series.trim() &&
      this.title.trim() &&
      lines.length > 0 &&
      lines.every((l) => l.skuCode.trim() && (l.quantity ?? 0) > 0);
    if (!valid) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.messages.warning'),
        detail: this.translate.instant('distribution.messages.invalid_form'),
      });
      return;
    }

    const body: SalesRequestDTO = {
      series: this.series.trim(),
      title: this.title.trim(),
      scheduleDate: this.scheduleDate?.toISOString(),
      description: this.description.trim() || undefined,
      items: lines.map((l) => ({
        skuCode: l.skuCode.trim(),
        quantity: l.quantity as number,
        description: l.description.trim() || undefined,
      })),
    };

    this.saving.set(true);
    this.service.create(body).subscribe({
      next: (created) => {
        this.saving.set(false);
        this.router.navigate(['/distribution/sales-request/view', created.id]);
      },
      error: (err) => {
        this.saving.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.messages.error'),
          detail:
            err?.error?.message ??
            this.translate.instant('distribution.messages.failed'),
        });
      },
    });
  }
}
